import { useEffect, useRef, useState } from 'react'
import { api } from '../api'
import { navigate } from '../router'
import type { AppSettings, Client } from '../../shared/types'
import { pushError, pushToast } from '../toast'
import { setAppTimezone, browserTz, timezoneOptions } from '../datetime'

// Global settings = ONLY the sender block (everything else is per-client),
// plus export/import for durability.
export default function GlobalSettings() {
  const [s, setS] = useState<AppSettings | null>(null)
  const [saving, setSaving] = useState(false)
  const [archived, setArchived] = useState<Client[]>([])
  const fileRef = useRef<HTMLInputElement>(null)

  async function loadArchived() {
    try {
      const all = await api.listClients(true)
      setArchived(all.filter((c) => c.archived))
    } catch {
      /* non-critical */
    }
  }

  useEffect(() => {
    ;(async () => {
      try {
        const loaded = await api.getSettings()
        setS(loaded)
        setAppTimezone(loaded.timezone)
      } catch (e) {
        pushError(`Couldn't load settings: ${(e as Error).message}`)
      }
    })()
    loadArchived()
  }, [])

  async function unarchive(c: Client) {
    try {
      await api.updateClient(c.id, { archived: 0 })
      setArchived((a) => a.filter((x) => x.id !== c.id))
      pushToast(`${c.simple_name} unarchived`)
    } catch (e) {
      pushError(`Couldn't unarchive: ${(e as Error).message}`)
    }
  }

  if (!s) return <div className="p-10 text-slate-400">Loading…</div>

  const set = <K extends keyof AppSettings>(k: K, v: AppSettings[K]) => setS({ ...s, [k]: v })

  async function save() {
    setSaving(true)
    try {
      await api.updateSettings(s!)
      setAppTimezone(s!.timezone)
      pushToast('Saved')
    } catch (e) {
      pushError(`Save failed: ${(e as Error).message}`)
    } finally {
      setSaving(false)
    }
  }

  async function doImport(file: File) {
    try {
      const text = await file.text()
      const data = JSON.parse(text)
      if (!confirm('Importing REPLACES all current data. Continue?')) return
      await api.importData(data)
      pushToast('Import complete — reloading')
      setTimeout(() => window.location.reload(), 600)
    } catch (e) {
      pushError(`Import failed: ${(e as Error).message}`)
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6 sm:py-10">
      <header className="mb-8 flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Settings</h1>
        <button className="text-sm text-slate-500 hover:text-slate-800" onClick={() => navigate('#/')}>
          ← Clients
        </button>
      </header>

      <section className="mb-8">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Sender (appears on every invoice)
        </h2>
        <div className="flex flex-col gap-4 rounded-lg border border-slate-200 bg-white p-4">
          <Field label="Name">
            <Text value={s.sender_name ?? ''} onChange={(v) => set('sender_name', v)} />
          </Field>
          <Field label="Company">
            <Text value={s.sender_company ?? ''} onChange={(v) => set('sender_company', v)} />
          </Field>
          <Field label="Address">
            <textarea
              className={inputCls + ' min-h-20'}
              value={s.sender_address ?? ''}
              onChange={(e) => set('sender_address', e.target.value)}
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Email">
              <Text value={s.sender_email ?? ''} onChange={(v) => set('sender_email', v)} />
            </Field>
            <Field label="Phone">
              <Text value={s.sender_phone ?? ''} onChange={(v) => set('sender_phone', v)} />
            </Field>
            <Field label="Website">
              <Text value={s.sender_website ?? ''} onChange={(v) => set('sender_website', v)} />
            </Field>
            <Field label="Business / Tax ID">
              <Text value={s.sender_tax_id ?? ''} onChange={(v) => set('sender_tax_id', v)} />
            </Field>
          </div>
          <Field label="Payment instructions (foot of invoice)">
            <textarea
              className={inputCls + ' min-h-20'}
              value={s.payment_instructions ?? ''}
              onChange={(e) => set('payment_instructions', e.target.value)}
            />
          </Field>
          <div className="flex justify-end">
            <button
              className="rounded-md bg-slate-900 px-4 py-2 font-medium text-white hover:bg-slate-700 disabled:opacity-50"
              onClick={save}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Timezone</h2>
        <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-sm text-slate-500">Controls what counts as “today”. Auto uses this browser’s zone ({browserTz()}).</p>
          <div className="flex items-center gap-3">
            <select
              className={inputCls + ' max-w-sm'}
              value={s.timezone ?? ''}
              onChange={(e) => set('timezone', e.target.value || null)}
            >
              <option value="">Auto (browser: {browserTz()})</option>
              {timezoneOptions().map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
            <button
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
              onClick={save}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Daily log calendar</h2>
        <div className="flex flex-col gap-4 rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-sm text-slate-500">Default visible window and snap interval for the Today calendar.</p>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Start time">
              <input
                type="time"
                className={inputCls + ' tabular'}
                value={s.daily_start_time ?? '08:00'}
                onChange={(e) => set('daily_start_time', e.target.value)}
              />
            </Field>
            <Field label="End time">
              <input
                type="time"
                className={inputCls + ' tabular'}
                value={s.daily_end_time ?? '16:00'}
                onChange={(e) => set('daily_end_time', e.target.value)}
              />
            </Field>
            <Field label="Interval">
              <select
                className={inputCls}
                value={s.daily_interval_minutes ?? 15}
                onChange={(e) => set('daily_interval_minutes', Number(e.target.value))}
              >
                {[5, 10, 15, 20, 30, 60].map((m) => (
                  <option key={m} value={m}>
                    {m} min
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="flex justify-end">
            <button
              className="rounded-md bg-slate-900 px-4 py-2 font-medium text-white hover:bg-slate-700 disabled:opacity-50"
              onClick={save}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </section>

      <section>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Data</h2>
        <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4 text-sm">
          <p className="text-slate-500">
            Export a full JSON backup (restorable) or a CSV of all entries. You should never be one bad
            migration away from losing billing history.
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              className="rounded-md border border-slate-300 px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50"
              href={api.exportUrl}
              download="timetracker-backup.json"
            >
              Export JSON
            </a>
            <a
              className="rounded-md border border-slate-300 px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50"
              href={api.exportCsvUrl}
              download="entries.csv"
            >
              Export entries CSV
            </a>
            <button
              className="rounded-md border border-slate-300 px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50"
              onClick={() => fileRef.current?.click()}
            >
              Import JSON…
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="application/json"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) void doImport(f)
                e.target.value = ''
              }}
            />
          </div>
        </div>
      </section>

      <section className="mt-8">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Archived clients</h2>
        <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm">
          {archived.length === 0 ? (
            <p className="text-slate-400">No archived clients.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-slate-100">
              {archived.map((c) => (
                <li key={c.id} className="flex items-center gap-3 py-2">
                  <span className="h-6 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: c.accent_color ?? '#94a3b8' }} />
                  <span className="flex-1">
                    <span className="font-medium text-slate-800">{c.simple_name}</span>
                    <span className="block text-xs text-slate-500">{c.legal_name}</span>
                  </span>
                  <button className="text-slate-500 hover:text-slate-800" onClick={() => navigate(`#/client/${c.id}`)}>
                    Open
                  </button>
                  <button className="font-medium text-[var(--accent)] hover:underline" onClick={() => unarchive(c)}>
                    Unarchive
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  )
}

const inputCls =
  'w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 focus:border-[var(--accent)]'

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700">{label}</span>
      {children}
    </label>
  )
}
function Text({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return <input className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} />
}
